import { Storage } from 'aws-amplify';
import {
  AddPrizeState,
  BaseTempParams,
  emptyConfigType,
  ImageMetadata,
  ParsedPrizeImgMetadata,
  PrizeState,
} from '../../../types/componentTypes/prizeComponentTypes';
import { CleanMetadataFuncParams } from '../../../utils/prizeUtils';
import moment from 'moment';
import { isNull } from 'lodash';

Storage.configure({
  customPrefix: {
    public: '',
    protected: '',
    private: '',
  },
});

export const processSubmitValues = async (
  values: AddPrizeState,
  configData: emptyConfigType
) => {
  const { tempParams, ...mainValues } = values;
  const data = transformToMap(mainValues);
  const finalResult = await handleInstantWin(data, configData);
  handleActiveStatus(finalResult);
  handleImagesMetadata(finalResult, tempParams);
  handleCNGMinAge(finalResult);

  return finalResult;
};

const handleInstantWin = async (data, configData) => {
  if (configData?.flow?.instantWin) {
    return await checkAndAddWinningLimit(data, configData);
  } else {
    delete data.pool_prize;
    return { ...data, cost: JSON.stringify(data.cost) };
  }
};

const handleCNGMinAge = (finalResult) => {
    switch ('min_age' in finalResult) {
        case typeof finalResult.min_age === 'string':
            finalResult.min_age = null;
            break;
        case isNull(finalResult.min_age):
            delete finalResult.min_age;
            break;
    }
}

const handleImagesMetadata = (finalResult, tempParams: BaseTempParams) => {
    const languages = Object.keys(JSON.parse(finalResult.name))
    const imgMetadataWillBeCreated = languages.some(lang => lang in tempParams.img_url)
    const hasImgUrls = finalResult.img_url?.length !== 0
    if (!hasImgUrls && !imgMetadataWillBeCreated) {
        tempParams.langUsingDefaultImgs = {}
    }
    if (finalResult.images_metadata && Object.keys(finalResult.images_metadata)?.length === 0) {
        finalResult.images_metadata = null
    }
    const defaultImgs = tempParams?.img_url?.[tempParams?.defaultLang]
    if(finalResult.images_metadata) {
        finalResult.images_metadata = cleanImgMetadataInfo(
            finalResult.images_metadata,
            tempParams,
            languages,
            defaultImgs,
        )
        return
    }
    if (!finalResult.images_metadata && hasImgUrls) {
        finalResult.images_metadata = createImgMetadataInfo(
            tempParams,
            defaultImgs,
            languages,
        )
    }
}

const getMetadataObjIfExists = (
    langCodeMetaArr: ParsedPrizeImgMetadata[], url: string,
 ) => langCodeMetaArr?.find((obj: ParsedPrizeImgMetadata) => obj?.url === url)

const clean = ({
    tempParams,
    langCode,
    objToClean,
    defaultImgs,
    existingMetadata,
 }: CleanMetadataFuncParams) => {
    const { langUsingDefaultImgs, defaultLang,  img_url } = tempParams
    const willUseDefaultImgs = langCode !== defaultLang && langUsingDefaultImgs?.[langCode]
    if (defaultImgs?.length !== 0 && (langCode === defaultLang || willUseDefaultImgs)) {
        objToClean[langCode] = defaultImgs
            ?.map((url, i) => prepareMetadata({
                i, url, metadata: getMetadataObjIfExists(existingMetadata?.[defaultLang], url)
            }))
    } else if (!willUseDefaultImgs && (langCode in img_url)) {
        objToClean[langCode] = img_url?.[langCode]
            ?.map((url, i) => prepareMetadata({
                i, url, metadata: getMetadataObjIfExists(existingMetadata?.[langCode], url)
            }))
    }
}

const cleanImgMetadataInfo = (
    metadata: ImageMetadata,
    tempParams: BaseTempParams,
    languages: string[],
    defaultImgs: string[],
 ) => {
    const imgsMetadataObj = {}
    const parsedMetadata = typeof metadata === 'string' ? JSON.parse(metadata) : metadata
    for (const langCode of languages) {
        const cleanParams: CleanMetadataFuncParams = {
            tempParams,
            defaultImgs,
            langCode,
            existingMetadata: parsedMetadata,
            objToClean: imgsMetadataObj
        }
        clean({...cleanParams})
    }
    return JSON.stringify(imgsMetadataObj)
}

const prepareMetadata = ({
    i, url, metadata
}: { i: number, url: string, metadata?: ParsedPrizeImgMetadata }) => {
    type ObjToReturn = {
        activeStatus: boolean;
        priority: number;
        ratio: string;
        size: string;
        url: string;
        alt?: string;
    }
    let objToReturn: ObjToReturn = {
        activeStatus: metadata?.activeStatus ?? false,
        priority: metadata?.priority ?? (i + 1),
        ratio: metadata?.ratio ?? 'vertical',
        size: metadata?.size ?? 'medium',
        url: metadata?.url ?? url,
    }
    if (metadata?.alt) objToReturn.alt = metadata?.alt
    return objToReturn
}

const createImgMetadataInfo = (
    tempParams: BaseTempParams,
    defaultImgs: string[]|undefined,
    languages: string[],
) => {
    const { langUsingDefaultImgs, defaultLang, img_url: imgUrls } = tempParams
    let imgsMetadataObj = {}
    for (const langCode of languages) {
        const willUseDefaultImgs = langCode !== defaultLang && langUsingDefaultImgs?.[langCode]
        if (defaultImgs !== undefined && (langCode === defaultLang || willUseDefaultImgs)) {
            imgsMetadataObj[langCode] = defaultImgs?.map((url, i) => prepareMetadata({ i, url }))
        } else {
            imgsMetadataObj[langCode] = imgUrls?.[langCode]?.map((url, i) => prepareMetadata({ i, url }))
        }
    }
    return JSON.stringify(imgsMetadataObj)
}

const transformToMap = (values: { [key: string]: any }) => {
  const stateValues = ['name', 'desc', 'short_desc', 'redeem_desc'];
  stateValues.forEach((value: string) => {
    const arrayValue = values[value];
    const mapValue = Object.assign({}, ...arrayValue);
    values[value] = JSON.stringify(mapValue);
  });
  return values;
};

const checkAndAddWinningLimit = async (
  values: { [key: string]: any },
  configData
) => {
  delete values.auto_redeem;
  delete values.cost;
  try {
    if (values.redemption_limit !== null && values.redemption_limit > 0) {
      const { prize_id, redemption_limit, configuration_id, pool_prize } = values;
      const poolPrize = typeof pool_prize === 'string' ? JSON.parse(pool_prize) : pool_prize;
      configData.flow.instantWin = {
        ...configData.flow.instantWin,
        params: {
          ...configData.flow.instantWin?.params,
        },
      };
      if (!poolPrize) {
        const winningLimits = {
          ...configData.flow.instantWin?.params?.winningLimitsPerPrize,
          [prize_id]: parseInt(redemption_limit),
        };
        configData.flow.instantWin.params = {
          ...configData.flow.instantWin.params,
          winningLimitsPerPrize: winningLimits,
        };
        delete values.redemption_limit;
      }
      const fileName = `${configuration_id}/conf.txt`;
      await Storage.put(fileName, JSON.stringify(configData), {
        contentType: 'text/plain',
      });
    } else if (
      (values.redemption_limit === null || values.redemption_limit <= 0) &&
      configData.flow.instantWin?.params?.winningLimitsPerPrize
    ) {
      const { prize_id, configuration_id } = values;
      const { [prize_id]: _, ...rest } =
        configData.flow.instantWin.params.winningLimitsPerPrize;
      if (Object.keys(rest).length > 0) {
        configData.flow.instantWin.params.winningLimitsPerPrize = { ...rest };
      } else {
        delete configData.flow.instantWin.params.winningLimitsPerPrize;
      }
      delete values.redemption_limit;

      const fileName = `${configuration_id}/conf.txt`;
      await Storage.put(fileName, JSON.stringify(configData), {
        contentType: 'text/plain',
      });
    }
    return values;
  } catch (err) {
    console.error('Error:', err);
  }
};

const handleActiveStatus = (prize: PrizeState) => {
    const startDate = typeof prize?.start_date === 'string' ? +prize?.start_date : prize?.start_date;
    const endDate = typeof prize?.end_date === 'string' ? +prize?.end_date : prize?.end_date;
    const today = moment().toDate().getTime();
    const shouldNotBeActive = startDate > today || today > endDate;

    if (prize?.active && shouldNotBeActive) prize.active = false;
};
